import {HttpVerb} from "../../../types/http-verb";
import {IServiceFactory} from "../../service-factory.interface";
import {IDotRezSessionTimerModel} from "./session-timer/dot-rez-session-timer.interface";
import {isHttpErrorStatusCode} from "../../http/http-helpers";
import {DotRezResponse} from "./dot-rez-response";

interface IFetchOptions {
    body?: any;
    doNotThrowOnError?: boolean;
}

export abstract class DotRezSessionBase {
    constructor(token: string,
                public readonly services: IServiceFactory,
                sessionTimer: IDotRezSessionTimerModel) {
        this._token = token;
        this._sessionTimer = sessionTimer;
    }

    private _buildUrl(path: string): string {
        return this.services.dotRezApi.buildDotRezUrl(path);
    }

    protected _sessionTimer: IDotRezSessionTimerModel;

    private _token: string;

    get token(): string {
        return this._token;
    }

    protected set token(value: string) {
        this._token = value;
    }

    protected async _fetchData<TData>(method: HttpVerb, path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {


        const url = this._buildUrl(path);
        const fetchResponse = await fetch(url, {
            method: method,
            body: options?.body && JSON.stringify(options.body),
            headers: this.services.dotRezApi.getDotRezApiRequestHeaders(this.token)
        });

        let json: any;
        try {
            json = await fetchResponse.json();
        } catch (err) {
            if(options?.doNotThrowOnError) {
                this.services.logger.error(`Failed to parse response from dotRez api ${url}`, err);
                const data: any = undefined;
                return new DotRezResponse<TData>(fetchResponse.status, data, fetchResponse.headers, undefined, [{code: "ResponseParseError"}]);
            } else {
                throw err;
            }
        }
        const dotRezResponse = new DotRezResponse<TData>(fetchResponse.status, json.data, fetchResponse.headers, json.messages, json.errors);

        if(isHttpErrorStatusCode(fetchResponse.status)) {
            const errorMsg = `${method} ${path} failed with status ${fetchResponse.status} - ${fetchResponse.statusText}`;

            if(dotRezResponse.hasDotRezError('nsk:Booking:NoBookingInState')) {
                this.services.logger.info(errorMsg, {
                    errors: json.errors,
                    request: options?.body
                });
            } else {
                this.services.logger.error(errorMsg, {
                    errors: json.errors,
                    request: options?.body
                });
            }
        }

        // if(dotRezResponse.isSessionExpired) {
        //     this._setExpired();
        //     throw new DotRezSessionExpiredException();
        // } else {
        //     this._sessionTimer.extendSessionLifetime();
        // }


        if(!options?.doNotThrowOnError) {
            dotRezResponse.throwIfError();
        }

        return dotRezResponse;
    }

    protected async get<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('GET', path, options);
    }

    protected async post<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('POST', path, options);
    }

    protected async put<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('PUT', path, options);
    }

    protected async patch<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('PATCH', path, options);
    }

    protected async delete<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('DELETE', path, options);
    }


}
